<template>
  <div v-if="broadcastSteps && broadcastSteps.length">
    <h1 class="text-2xl sm:text-3xl font-bold text-center mt-6 sm:mt-0">
      How to create a broadcast on www.TheBrokerSite.com
    </h1>

    <p class="mb-10 mt-5 text-gray-600 text-center max-w-2xl mx-auto text-sm sm:text-base">
      Creating a broadcast is a simple and efficient way to connect with your audience.
      With a streamlined process and intuitive interface, you can prepare and send your message in just a few steps. 
      Follow the guide below to get started:
    </p>

    <div class="space-y-8">
      <div v-for="(step, index) in broadcastSteps" :key="index" class="bg-white p-6 rounded-lg shadow-md">
        <!-- CONTENT -->
        <div class="flex flex-col lg:flex-row items-center gap-8">
          <!-- TEXT (2/5) -->
          <div class="w-full lg:w-2/6">
            <div v-if="step.thumbnail" class=" rounded-xl flex items-center justify-center p-3 bg-white ">
              <img :src="step.thumbnail" class="w-24 object-contain " alt="Step 1 Thumbnail">
            </div>
            <h2 class="text-xl font-bold text-red-700 mb-2 text-center">
              {{ step.title }}
            </h2>
            <p class="text-gray-600 text-sm px-4 text-center leading-5">
              {{ step.description }}
            </p>
          </div>

          <!-- IMAGE (3/5) -->
          <div class="w-full lg:w-4/6 flex justify-center">
            <a :href="step.image" target="_blank" rel="noopener noreferrer">
              <img :src="step.image" :alt="step.title" class="rounded-xl shadow-md border w-full cursor-pointer" />
            </a>
          </div>
        </div>
      </div>
    </div>
    <template>
      <FAQSection :faqs="faqs" />
    </template>
  </div>
</template>

<script>

import FAQSection from '@/components/FAQSection.vue';
import { useSchemaOrg } from '../composables/useSchema.org.js';
import { mapState } from 'vuex';


export default {
  name: 'HowtoCreateaBroadcast',
  components: {
    FAQSection
  },
  data: () => ({
    broadcastSteps: [
      {
        title: 'Open the Compose Window',
        description: 'Go to the Home or Broadcast page and click the “Compose” button. This will open the broadcast creation form in a pop-up window (modal).',
        image: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-1.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-1-thumbnail.webp'
      },
      {
        title: 'Fill in the Required Details',
        description: 'Enter all the necessary information. Validation messages will appear if required fields are missing.',
        image: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-2.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-2-thumbnail.webp'
      },
      {
        title: 'Add Images (Optional)',
        description: 'Upload up to five images to make your broadcast more engaging and visually appealing.',
        image: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-3.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-3-thumbnail.webp'
      },
      {
        title: 'Review and Send',
        description: 'Once all required fields are completed, click the “Send” button to publish your broadcast.',
        image: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-4.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-4-thumbnail.webp'
      },
      {
        title: 'Confirmation Message',
        description: 'After sending, a confirmation message will appear in the upper-right corner of your screen.',
        image: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-5.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/create_a_broadcast/compose-broadcast-step-5-thumbnail.webp'
      }
    ]
  }),
  mounted() {
    const helpSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to create a broadcast on www.TheBrokerSite.com",
      description: "Creating a broadcast is a simple and efficient way to connect with your audience. With a streamlined process and intuitive interface, you can prepare and send your message in just a few steps. Follow the guide below to get started:",
      image: this.broadcastSteps.map(step => step.image),

      step: this.broadcastSteps.map((step, index) => ({
        "@type": "HowToStep",
        name: step.title,
        text: step.description,
        image: step.image,
        position: index + 1
      })),

      mainEntity: {
        "@type": "FAQPage",
        mainEntity: this.faqs.map(faq => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer
          }
        }))
      }
    };
    useSchemaOrg(helpSchema);
  },
  computed: {
    ...mapState(['frequentlyAskedQuestions']),
    faqs() {
      return this.frequentlyAskedQuestions['how_to_create_a_broadcast']
    }
  }
}
</script>
